import React, { useState, useMemo } from 'react';
import { Button } from '../ui/Button';
import { getBayLetter } from '../../utils/warehouseUtils';
import type {
  WarehouseZone,
  WarehouseRack,
  WarehouseBin,
  RackCreateData,
  RackUpdateData,
  MeasurementUnit,
} from '../../types/warehouse';
import { RackBinsPanel } from './RackBinsPanel';
import {
  Plus,
  Grid3X3,
  Layers,
  Edit2,
  Trash2,
  X,
  AlertTriangle,
  Filter,
  Box,
  ArrowUpDown,
  ChevronRight,
  Eye,
  Copy,
} from 'lucide-react';

interface WarehouseRacksTabProps {
  zones: WarehouseZone[];
  racks: WarehouseRack[];
  bins: WarehouseBin[];
  unit: MeasurementUnit;
  onCreateRack: (data: Omit<RackCreateData, 'warehouse_id'>) => Promise<WarehouseRack | null>;
  onUpdateRack: (id: string, data: RackUpdateData) => Promise<WarehouseRack | null>;
  onDeleteRack: (id: string) => Promise<boolean>;
  onDuplicateRacks: (sourceRack: WarehouseRack, count: number) => Promise<WarehouseRack[]>;
}

type SortField = 'rack_code' | 'zone' | 'levels' | 'bays' | 'bins';
type SortDirection = 'asc' | 'desc';

export const WarehouseRacksTab: React.FC<WarehouseRacksTabProps> = ({
  zones,
  racks,
  bins,
  unit,
  onCreateRack,
  onUpdateRack,
  onDeleteRack,
  onDuplicateRacks,
}) => {
  const [showCreateModal, setShowCreateModal] = useState(false);
  const [editingRack, setEditingRack] = useState<WarehouseRack | null>(null);
  const [deletingRack, setDeletingRack] = useState<WarehouseRack | null>(null);
  const [duplicatingRack, setDuplicatingRack] = useState<WarehouseRack | null>(null);
  const [viewingRack, setViewingRack] = useState<WarehouseRack | null>(null);
  const [filterZone, setFilterZone] = useState<string>('all');
  const [sortField, setSortField] = useState<SortField>('rack_code');
  const [sortDirection, setSortDirection] = useState<SortDirection>('asc');

  const getBinsInRack = (rackId: string) => bins.filter((b) => b.rack_id === rackId);
  const getZoneForRack = (zoneId: string | null) => zones.find((z) => z.id === zoneId);

  const filteredAndSortedRacks = useMemo(() => {
    let result = [...racks];

    if (filterZone !== 'all') {
      if (filterZone === 'unassigned') {
        result = result.filter((r) => !r.zone_id);
      } else {
        result = result.filter((r) => r.zone_id === filterZone);
      }
    }

    result.sort((a, b) => {
      let comparison = 0;

      switch (sortField) {
        case 'rack_code':
          comparison = a.rack_code.localeCompare(b.rack_code);
          break;
        case 'zone':
          const zoneA = getZoneForRack(a.zone_id)?.zone_code || 'ZZZ';
          const zoneB = getZoneForRack(b.zone_id)?.zone_code || 'ZZZ';
          comparison = zoneA.localeCompare(zoneB);
          break;
        case 'levels':
          comparison = a.levels - b.levels;
          break;
        case 'bays':
          comparison = a.bays - b.bays;
          break;
        case 'bins':
          comparison = getBinsInRack(a.id).length - getBinsInRack(b.id).length;
          break;
      }

      return sortDirection === 'asc' ? comparison : -comparison;
    });

    return result;
  }, [racks, filterZone, sortField, sortDirection, zones, bins]);

  const handleSort = (field: SortField) => {
    if (sortField === field) {
      setSortDirection(sortDirection === 'asc' ? 'desc' : 'asc');
    } else {
      setSortField(field);
      setSortDirection('asc');
    }
  };

  const handleCreateRack = async (data: Omit<RackCreateData, 'warehouse_id'>) => {
    const rack = await onCreateRack(data);
    if (rack) {
      setShowCreateModal(false);
    }
    return rack;
  };

  const handleUpdateRack = async (id: string, data: RackUpdateData) => {
    const rack = await onUpdateRack(id, data);
    if (rack) {
      setEditingRack(null);
    }
    return rack;
  };

  const handleDeleteRack = async (id: string) => {
    const success = await onDeleteRack(id);
    if (success) {
      setDeletingRack(null);
    }
    return success;
  };

  const unassignedCount = racks.filter((r) => !r.zone_id).length;

  if (racks.length === 0) {
    return (
      <div className="text-center py-16">
        <div className="w-20 h-20 bg-emerald-100 dark:bg-emerald-900/30 rounded-2xl flex items-center justify-center mx-auto mb-6">
          <Grid3X3 className="h-10 w-10 text-emerald-600 dark:text-emerald-400" />
        </div>
        <h3 className="text-xl font-semibold text-gray-900 dark:text-gray-100 mb-2">
          No Racks Created
        </h3>
        <p className="text-gray-600 dark:text-gray-400 mb-6 max-w-md mx-auto">
          Racks are the physical storage units in your warehouse. Each rack can have multiple levels
          and bays to organize your inventory.
        </p>
        <Button variant="primary" onClick={() => setShowCreateModal(true)}>
          <Plus className="h-4 w-4 mr-2" />
          Create First Rack
        </Button>

        {showCreateModal && (
          <CreateRackModal
            zones={zones}
            existingRacks={racks}
            unit={unit}
            onSubmit={handleCreateRack}
            onClose={() => setShowCreateModal(false)}
          />
        )}
      </div>
    );
  }

  return (
    <div className="space-y-6">
      <div className="flex flex-col sm:flex-row sm:items-center justify-between gap-4">
        <div>
          <h3 className="text-lg font-semibold text-gray-900 dark:text-gray-100">
            Racks ({racks.length})
          </h3>
          <p className="text-sm text-gray-600 dark:text-gray-400">
            Manage storage racks and their configurations
          </p>
        </div>
        <div className="flex items-center gap-3">
          <div className="relative">
            <Filter className="absolute left-3 top-1/2 -translate-y-1/2 h-4 w-4 text-gray-400" />
            <select
              value={filterZone}
              onChange={(e) => setFilterZone(e.target.value)}
              className="pl-9 pr-8 py-2 rounded-lg border border-gray-300 dark:border-gray-600 bg-white dark:bg-gray-700 text-gray-900 dark:text-gray-100 text-sm focus:outline-none focus:ring-2 focus:ring-blue-500 appearance-none cursor-pointer"
            >
              <option value="all">All Zones</option>
              <option value="unassigned">Unassigned ({unassignedCount})</option>
              {zones.map((zone) => (
                <option key={zone.id} value={zone.id}>
                  Zone {zone.zone_code} ({racks.filter((r) => r.zone_id === zone.id).length})
                </option>
              ))}
            </select>
          </div>
          <Button variant="primary" onClick={() => setShowCreateModal(true)}>
            <Plus className="h-4 w-4 mr-2" />
            Add Rack
          </Button>
        </div>
      </div>

      <div className="bg-white dark:bg-gray-800 border border-gray-200 dark:border-gray-700 rounded-xl overflow-hidden">
        <div className="overflow-x-auto">
          <table className="w-full">
            <thead>
              <tr className="bg-gray-50 dark:bg-gray-700/50 border-b border-gray-200 dark:border-gray-700">
                <SortableHeader
                  label="Rack"
                  field="rack_code"
                  currentField={sortField}
                  direction={sortDirection}
                  onSort={handleSort}
                />
                <SortableHeader
                  label="Zone"
                  field="zone"
                  currentField={sortField}
                  direction={sortDirection}
                  onSort={handleSort}
                />
                <SortableHeader
                  label="Levels"
                  field="levels"
                  currentField={sortField}
                  direction={sortDirection}
                  onSort={handleSort}
                  className="text-center"
                />
                <SortableHeader
                  label="Bays"
                  field="bays"
                  currentField={sortField}
                  direction={sortDirection}
                  onSort={handleSort}
                  className="text-center"
                />
                <SortableHeader
                  label="Bins"
                  field="bins"
                  currentField={sortField}
                  direction={sortDirection}
                  onSort={handleSort}
                  className="text-center"
                />
                <th className="px-4 py-3 text-right text-xs font-medium text-gray-500 dark:text-gray-400 uppercase tracking-wider">
                  Actions
                </th>
              </tr>
            </thead>
            <tbody className="divide-y divide-gray-200 dark:divide-gray-700">
              {filteredAndSortedRacks.map((rack) => {
                const zone = getZoneForRack(rack.zone_id);
                const rackBins = getBinsInRack(rack.id);
                const totalCapacity = rack.levels * rack.bays;

                return (
                  <tr
                    key={rack.id}
                    className="hover:bg-gray-50 dark:hover:bg-gray-700/30 transition-colors"
                  >
                    <td className="px-4 py-4">
                      <div className="flex items-center gap-3">
                        <div className="w-10 h-10 bg-emerald-100 dark:bg-emerald-900/30 rounded-lg flex items-center justify-center">
                          <Grid3X3 className="h-5 w-5 text-emerald-600 dark:text-emerald-400" />
                        </div>
                        <div>
                          <div className="font-medium text-gray-900 dark:text-gray-100">
                            {rack.rack_code}
                          </div>
                          {rack.rack_name && (
                            <div className="text-sm text-gray-500 dark:text-gray-400">
                              {rack.rack_name}
                            </div>
                          )}
                        </div>
                      </div>
                    </td>
                    <td className="px-4 py-4">
                      {zone ? (
                        <div className="flex items-center gap-2">
                          <span
                            className="w-3 h-3 rounded-full"
                            style={{ backgroundColor: zone.color }}
                          />
                          <span className="text-gray-900 dark:text-gray-100">
                            Zone {zone.zone_code}
                          </span>
                        </div>
                      ) : (
                        <span className="text-gray-400 dark:text-gray-500 italic">Unassigned</span>
                      )}
                    </td>
                    <td className="px-4 py-4 text-center">
                      <span className="text-gray-900 dark:text-gray-100">{rack.levels}</span>
                    </td>
                    <td className="px-4 py-4 text-center">
                      <span className="text-gray-900 dark:text-gray-100">{rack.bays}</span>
                    </td>
                    <td className="px-4 py-4 text-center">
                      <div className="flex items-center justify-center gap-1">
                        <Box className="h-4 w-4 text-gray-400" />
                        <span className="text-gray-900 dark:text-gray-100">{rackBins.length}</span>
                        <span className="text-gray-400">/ {totalCapacity}</span>
                      </div>
                    </td>
                    <td className="px-4 py-4">
                      <div className="flex items-center justify-end gap-1">
                        <button
                          onClick={() => setViewingRack(rack)}
                          className="p-2 rounded-lg text-gray-500 hover:text-emerald-600 hover:bg-emerald-50 dark:hover:bg-emerald-900/20 transition-colors"
                          title="View bins"
                        >
                          <Eye className="h-4 w-4" />
                        </button>
                        <button
                          onClick={() => setDuplicatingRack(rack)}
                          className="p-2 rounded-lg text-gray-500 hover:text-cyan-600 hover:bg-cyan-50 dark:hover:bg-cyan-900/20 transition-colors"
                          title="Duplicate rack"
                        >
                          <Copy className="h-4 w-4" />
                        </button>
                        <button
                          onClick={() => setEditingRack(rack)}
                          className="p-2 rounded-lg text-gray-500 hover:text-blue-600 hover:bg-blue-50 dark:hover:bg-blue-900/20 transition-colors"
                          title="Edit rack"
                        >
                          <Edit2 className="h-4 w-4" />
                        </button>
                        <button
                          onClick={() => setDeletingRack(rack)}
                          className="p-2 rounded-lg text-gray-500 hover:text-red-600 hover:bg-red-50 dark:hover:bg-red-900/20 transition-colors"
                          title="Delete rack"
                        >
                          <Trash2 className="h-4 w-4" />
                        </button>
                      </div>
                    </td>
                  </tr>
                );
              })}
            </tbody>
          </table>
        </div>

        {filteredAndSortedRacks.length === 0 && (
          <div className="text-center py-12">
            <Filter className="h-8 w-8 text-gray-400 mx-auto mb-3" />
            <p className="text-gray-600 dark:text-gray-400">
              No racks match the current filter
            </p>
          </div>
        )}
      </div>

      <div className="grid grid-cols-1 sm:grid-cols-3 gap-4">
        <div className="bg-gray-50 dark:bg-gray-800/50 rounded-xl p-4">
          <div className="flex items-center gap-3">
            <div className="w-10 h-10 bg-blue-100 dark:bg-blue-900/30 rounded-lg flex items-center justify-center">
              <Layers className="h-5 w-5 text-blue-600 dark:text-blue-400" />
            </div>
            <div>
              <div className="text-2xl font-bold text-gray-900 dark:text-gray-100">
                {racks.reduce((sum, r) => sum + r.levels, 0)}
              </div>
              <div className="text-sm text-gray-500 dark:text-gray-400">Total Levels</div>
            </div>
          </div>
        </div>
        <div className="bg-gray-50 dark:bg-gray-800/50 rounded-xl p-4">
          <div className="flex items-center gap-3">
            <div className="w-10 h-10 bg-emerald-100 dark:bg-emerald-900/30 rounded-lg flex items-center justify-center">
              <Grid3X3 className="h-5 w-5 text-emerald-600 dark:text-emerald-400" />
            </div>
            <div>
              <div className="text-2xl font-bold text-gray-900 dark:text-gray-100">
                {racks.reduce((sum, r) => sum + r.bays, 0)}
              </div>
              <div className="text-sm text-gray-500 dark:text-gray-400">Total Bays</div>
            </div>
          </div>
        </div>
        <div className="bg-gray-50 dark:bg-gray-800/50 rounded-xl p-4">
          <div className="flex items-center gap-3">
            <div className="w-10 h-10 bg-amber-100 dark:bg-amber-900/30 rounded-lg flex items-center justify-center">
              <Box className="h-5 w-5 text-amber-600 dark:text-amber-400" />
            </div>
            <div>
              <div className="text-2xl font-bold text-gray-900 dark:text-gray-100">
                {racks.reduce((sum, r) => sum + r.levels * r.bays, 0)}
              </div>
              <div className="text-sm text-gray-500 dark:text-gray-400">Max Bin Capacity</div>
            </div>
          </div>
        </div>
      </div>

      {showCreateModal && (
        <CreateRackModal
          zones={zones}
          existingRacks={racks}
          unit={unit}
          onSubmit={handleCreateRack}
          onClose={() => setShowCreateModal(false)}
        />
      )}

      {editingRack && (
        <EditRackModal
          rack={editingRack}
          zones={zones}
          unit={unit}
          onSubmit={(data) => handleUpdateRack(editingRack.id, data)}
          onClose={() => setEditingRack(null)}
        />
      )}

      {deletingRack && (
        <DeleteRackModal
          rack={deletingRack}
          binCount={getBinsInRack(deletingRack.id).length}
          zone={getZoneForRack(deletingRack.zone_id)}
          onConfirm={() => handleDeleteRack(deletingRack.id)}
          onCancel={() => setDeletingRack(null)}
        />
      )}

      {duplicatingRack && (
        <DuplicateRackModal
          rack={duplicatingRack}
          zone={getZoneForRack(duplicatingRack.zone_id)}
          existingRacks={racks}
          onConfirm={async (count) => {
            const created = await onDuplicateRacks(duplicatingRack, count);
            if (created.length > 0) {
              setDuplicatingRack(null);
            }
            return created.length > 0;
          }}
          onCancel={() => setDuplicatingRack(null)}
        />
      )}

      {viewingRack && (
        <div className="fixed inset-0 z-50 flex items-center justify-center p-4">
          <div
            className="absolute inset-0 bg-black/50 backdrop-blur-sm"
            onClick={() => setViewingRack(null)}
          />
          <div className="relative w-full max-w-3xl max-h-[90vh] overflow-y-auto">
            <RackBinsPanel
              rack={viewingRack}
              bins={getBinsInRack(viewingRack.id)}
              zone={getZoneForRack(viewingRack.zone_id)}
              onClose={() => setViewingRack(null)}
            />
          </div>
        </div>
      )}
    </div>
  );
};

interface SortableHeaderProps {
  label: string;
  field: SortField;
  currentField: SortField;
  direction: SortDirection;
  onSort: (field: SortField) => void;
  className?: string;
}

const SortableHeader: React.FC<SortableHeaderProps> = ({
  label,
  field,
  currentField,
  direction,
  onSort,
  className = '',
}) => {
  const isActive = field === currentField;

  return (
    <th
      className={`px-4 py-3 text-xs font-medium text-gray-500 dark:text-gray-400 uppercase tracking-wider cursor-pointer hover:bg-gray-100 dark:hover:bg-gray-600/50 transition-colors ${className}`}
      onClick={() => onSort(field)}
    >
      <div className={`flex items-center gap-1 ${className.includes('text-center') ? 'justify-center' : ''}`}>
        {label}
        <ArrowUpDown
          className={`h-3.5 w-3.5 transition-colors ${
            isActive ? 'text-blue-600 dark:text-blue-400' : 'text-gray-400'
          }`}
        />
      </div>
    </th>
  );
};

interface CreateRackModalProps {
  zones: WarehouseZone[];
  existingRacks: WarehouseRack[];
  onSubmit: (data: Omit<RackCreateData, 'warehouse_id'>) => Promise<WarehouseRack | null>;
  onClose: () => void;
}

interface CreateRackModalPropsExtended extends CreateRackModalProps {
  unit?: MeasurementUnit;
}

const CreateRackModal: React.FC<CreateRackModalPropsExtended> = ({
  zones,
  existingRacks,
  onSubmit,
  onClose,
  unit = 'm',
}) => {
  const [rackName, setRackName] = useState('');
  const [selectedZone, setSelectedZone] = useState<string>('');
  const [levels, setLevels] = useState(4);
  const [bays, setBays] = useState(6);
  const [rackLength, setRackLength] = useState<number>(unit === 'cm' ? 300 : 3);
  const [rackWidth, setRackWidth] = useState<number>(unit === 'cm' ? 100 : 1);
  const [rackHeight, setRackHeight] = useState<number | null>(null);
  const [saving, setSaving] = useState(false);

  const unitLabel = unit === 'cm' ? 'cm' : 'm';
  const minLength = unit === 'cm' ? 10 : 0.1;
  const maxLength = unit === 'cm' ? 2000 : 20;
  const stepLength = unit === 'cm' ? 10 : 0.1;
  const minWidth = unit === 'cm' ? 10 : 0.1;
  const maxWidth = unit === 'cm' ? 1000 : 10;
  const stepWidth = unit === 'cm' ? 10 : 0.1;

  const nextRackNumber = existingRacks.length + 1;

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    setSaving(true);
    await onSubmit({
      rack_name: rackName.trim() || undefined,
      zone_id: selectedZone || undefined,
      levels,
      bays,
      rack_length: rackLength,
      rack_depth: rackWidth,
      rack_height: rackHeight,
    });
    setSaving(false);
  };

  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center">
      <div className="absolute inset-0 bg-black/50 backdrop-blur-sm" onClick={onClose} />

      <div className="relative bg-white dark:bg-gray-800 rounded-2xl shadow-2xl w-full max-w-lg mx-4 overflow-hidden">
        <div className="bg-gradient-to-r from-emerald-600 to-teal-600 px-6 py-5">
          <div className="flex items-center justify-between">
            <div className="flex items-center gap-3">
              <div className="w-10 h-10 bg-white/20 rounded-xl flex items-center justify-center">
                <Grid3X3 className="h-5 w-5 text-white" />
              </div>
              <div>
                <h2 className="text-xl font-bold text-white">Create Rack</h2>
                <p className="text-emerald-100 text-sm">Add a new storage rack</p>
              </div>
            </div>
            <button
              onClick={onClose}
              className="p-2 rounded-lg text-white/80 hover:text-white hover:bg-white/10 transition-colors"
            >
              <X className="h-5 w-5" />
            </button>
          </div>
        </div>

        <form onSubmit={handleSubmit} className="p-6 space-y-6">
          <div className="grid grid-cols-2 gap-4">
            <div className="col-span-2">
              <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-2">
                Rack Name <span className="text-gray-400">(optional)</span>
              </label>
              <input
                type="text"
                value={rackName}
                onChange={(e) => setRackName(e.target.value)}
                placeholder="e.g., Electronics, Bulk Storage"
                className="w-full px-4 py-3 rounded-lg border border-gray-300 dark:border-gray-600 bg-white dark:bg-gray-700 text-gray-900 dark:text-gray-100 focus:outline-none focus:ring-2 focus:ring-emerald-500"
              />
            </div>

            <div className="col-span-2">
              <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-2">
                Assign to Zone <span className="text-gray-400">(optional)</span>
              </label>
              <select
                value={selectedZone}
                onChange={(e) => setSelectedZone(e.target.value)}
                className="w-full px-4 py-3 rounded-lg border border-gray-300 dark:border-gray-600 bg-white dark:bg-gray-700 text-gray-900 dark:text-gray-100 focus:outline-none focus:ring-2 focus:ring-emerald-500"
              >
                <option value="">No Zone (Unassigned)</option>
                {zones.map((zone) => (
                  <option key={zone.id} value={zone.id}>
                    Zone {zone.zone_code}
                    {zone.zone_name ? ` - ${zone.zone_name}` : ''}
                  </option>
                ))}
              </select>
            </div>

            <div>
              <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-2">
                Length ({unitLabel})
              </label>
              <div className="flex items-center gap-2">
                <button
                  type="button"
                  onClick={() => setRackLength(Math.max(minLength, rackLength - stepLength))}
                  className="w-10 h-10 rounded-lg border border-gray-300 dark:border-gray-600 flex items-center justify-center text-gray-600 dark:text-gray-400 hover:bg-gray-100 dark:hover:bg-gray-700 transition-colors"
                >
                  -
                </button>
                <input
                  type="number"
                  min={minLength}
                  max={maxLength}
                  step={stepLength}
                  value={rackLength}
                  onChange={(e) => setRackLength(Math.max(minLength, Math.min(maxLength, parseFloat(e.target.value) || minLength)))}
                  className="flex-1 px-4 py-2 rounded-lg border border-gray-300 dark:border-gray-600 bg-white dark:bg-gray-700 text-gray-900 dark:text-gray-100 text-center focus:outline-none focus:ring-2 focus:ring-emerald-500"
                />
                <button
                  type="button"
                  onClick={() => setRackLength(Math.min(maxLength, rackLength + stepLength))}
                  className="w-10 h-10 rounded-lg border border-gray-300 dark:border-gray-600 flex items-center justify-center text-gray-600 dark:text-gray-400 hover:bg-gray-100 dark:hover:bg-gray-700 transition-colors"
                >
                  +
                </button>
              </div>
            </div>

            <div>
              <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-2">
                Width ({unitLabel})
              </label>
              <div className="flex items-center gap-2">
                <button
                  type="button"
                  onClick={() => setRackWidth(Math.max(minWidth, rackWidth - stepWidth))}
                  className="w-10 h-10 rounded-lg border border-gray-300 dark:border-gray-600 flex items-center justify-center text-gray-600 dark:text-gray-400 hover:bg-gray-100 dark:hover:bg-gray-700 transition-colors"
                >
                  -
                </button>
                <input
                  type="number"
                  min={minWidth}
                  max={maxWidth}
                  step={stepWidth}
                  value={rackWidth}
                  onChange={(e) => setRackWidth(Math.max(minWidth, Math.min(maxWidth, parseFloat(e.target.value) || minWidth)))}
                  className="flex-1 px-4 py-2 rounded-lg border border-gray-300 dark:border-gray-600 bg-white dark:bg-gray-700 text-gray-900 dark:text-gray-100 text-center focus:outline-none focus:ring-2 focus:ring-emerald-500"
                />
                <button
                  type="button"
                  onClick={() => setRackWidth(Math.min(maxWidth, rackWidth + stepWidth))}
                  className="w-10 h-10 rounded-lg border border-gray-300 dark:border-gray-600 flex items-center justify-center text-gray-600 dark:text-gray-400 hover:bg-gray-100 dark:hover:bg-gray-700 transition-colors"
                >
                  +
                </button>
              </div>
            </div>

            <div className="col-span-2">
              <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-2">
                Height ({unitLabel}) <span className="text-gray-400">(optional)</span>
              </label>
              <div className="flex items-center gap-2">
                <button
                  type="button"
                  onClick={() => setRackHeight(rackHeight !== null ? Math.max(minLength, rackHeight - stepLength) : null)}
                  disabled={rackHeight === null}
                  className="w-10 h-10 rounded-lg border border-gray-300 dark:border-gray-600 flex items-center justify-center text-gray-600 dark:text-gray-400 hover:bg-gray-100 dark:hover:bg-gray-700 transition-colors disabled:opacity-50"
                >
                  -
                </button>
                <input
                  type="number"
                  min={minLength}
                  max={maxLength}
                  step={stepLength}
                  value={rackHeight ?? ''}
                  placeholder="Not set"
                  onChange={(e) => setRackHeight(e.target.value ? parseFloat(e.target.value) : null)}
                  className="flex-1 px-4 py-2 rounded-lg border border-gray-300 dark:border-gray-600 bg-white dark:bg-gray-700 text-gray-900 dark:text-gray-100 text-center focus:outline-none focus:ring-2 focus:ring-emerald-500"
                />
                <button
                  type="button"
                  onClick={() => setRackHeight(rackHeight !== null ? Math.min(maxLength, rackHeight + stepLength) : (unit === 'cm' ? 200 : 2))}
                  className="w-10 h-10 rounded-lg border border-gray-300 dark:border-gray-600 flex items-center justify-center text-gray-600 dark:text-gray-400 hover:bg-gray-100 dark:hover:bg-gray-700 transition-colors"
                >
                  +
                </button>
                {rackHeight !== null && (
                  <button
                    type="button"
                    onClick={() => setRackHeight(null)}
                    className="px-3 py-2 text-sm text-gray-500 hover:text-red-500 transition-colors"
                  >
                    Clear
                  </button>
                )}
              </div>
              <p className="mt-1 text-xs text-gray-500 dark:text-gray-400">Physical rack height (not shown on 2D map)</p>
            </div>

            <div>
              <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-2">
                Number of Levels
              </label>
              <div className="flex items-center gap-2">
                <button
                  type="button"
                  onClick={() => setLevels(Math.max(1, levels - 1))}
                  className="w-10 h-10 rounded-lg border border-gray-300 dark:border-gray-600 flex items-center justify-center text-gray-600 dark:text-gray-400 hover:bg-gray-100 dark:hover:bg-gray-700 transition-colors"
                >
                  -
                </button>
                <input
                  type="number"
                  min={1}
                  max={20}
                  value={levels}
                  onChange={(e) => setLevels(Math.max(1, Math.min(20, parseInt(e.target.value) || 1)))}
                  className="flex-1 px-4 py-2 rounded-lg border border-gray-300 dark:border-gray-600 bg-white dark:bg-gray-700 text-gray-900 dark:text-gray-100 text-center focus:outline-none focus:ring-2 focus:ring-emerald-500"
                />
                <button
                  type="button"
                  onClick={() => setLevels(Math.min(20, levels + 1))}
                  className="w-10 h-10 rounded-lg border border-gray-300 dark:border-gray-600 flex items-center justify-center text-gray-600 dark:text-gray-400 hover:bg-gray-100 dark:hover:bg-gray-700 transition-colors"
                >
                  +
                </button>
              </div>
              <p className="mt-1 text-xs text-gray-500 dark:text-gray-400">Vertical shelves (1-20)</p>
            </div>

            <div>
              <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-2">
                Number of Bays
              </label>
              <div className="flex items-center gap-2">
                <button
                  type="button"
                  onClick={() => setBays(Math.max(1, bays - 1))}
                  className="w-10 h-10 rounded-lg border border-gray-300 dark:border-gray-600 flex items-center justify-center text-gray-600 dark:text-gray-400 hover:bg-gray-100 dark:hover:bg-gray-700 transition-colors"
                >
                  -
                </button>
                <input
                  type="number"
                  min={1}
                  max={50}
                  value={bays}
                  onChange={(e) => setBays(Math.max(1, Math.min(50, parseInt(e.target.value) || 1)))}
                  className="flex-1 px-4 py-2 rounded-lg border border-gray-300 dark:border-gray-600 bg-white dark:bg-gray-700 text-gray-900 dark:text-gray-100 text-center focus:outline-none focus:ring-2 focus:ring-emerald-500"
                />
                <button
                  type="button"
                  onClick={() => setBays(Math.min(50, bays + 1))}
                  className="w-10 h-10 rounded-lg border border-gray-300 dark:border-gray-600 flex items-center justify-center text-gray-600 dark:text-gray-400 hover:bg-gray-100 dark:hover:bg-gray-700 transition-colors"
                >
                  +
                </button>
              </div>
              <p className="mt-1 text-xs text-gray-500 dark:text-gray-400">Horizontal sections (1-50)</p>
            </div>
          </div>

          <div className="bg-gray-50 dark:bg-gray-700/50 rounded-lg p-4">
            <div className="flex items-start gap-4">
              <div className="w-12 h-12 bg-emerald-100 dark:bg-emerald-900/30 rounded-xl flex items-center justify-center flex-shrink-0">
                <Grid3X3 className="h-6 w-6 text-emerald-600 dark:text-emerald-400" />
              </div>
              <div className="flex-1">
                <div className="font-medium text-gray-900 dark:text-gray-100">
                  R{String(nextRackNumber).padStart(2, '0')}
                  {rackName && ` - ${rackName}`}
                </div>
                <div className="text-sm text-gray-500 dark:text-gray-400 mt-1">
                  {levels} level{levels !== 1 ? 's' : ''} x {bays} bay{bays !== 1 ? 's' : ''} ={' '}
                  <strong>{levels * bays}</strong> bin positions
                </div>
                <div className="text-xs text-gray-400 dark:text-gray-500 mt-1">
                  Bins: {getBayLetter(0)}1 - {getBayLetter(bays - 1)}{levels}
                </div>
              </div>
            </div>
          </div>

          <div className="flex gap-3">
            <Button type="button" variant="secondary" onClick={onClose} className="flex-1">
              Cancel
            </Button>
            <Button type="submit" variant="primary" loading={saving} className="flex-1">
              Create Rack
            </Button>
          </div>
        </form>
      </div>
    </div>
  );
};

interface EditRackModalProps {
  rack: WarehouseRack;
  zones: WarehouseZone[];
  unit?: MeasurementUnit;
  onSubmit: (data: RackUpdateData) => Promise<WarehouseRack | null>;
  onClose: () => void;
}

const EditRackModal: React.FC<EditRackModalProps> = ({ rack, zones, unit = 'm', onSubmit, onClose }) => {
  const [rackName, setRackName] = useState(rack.rack_name || '');
  const [selectedZone, setSelectedZone] = useState<string>(rack.zone_id || '');
  const [levels, setLevels] = useState(rack.levels);
  const [bays, setBays] = useState(rack.bays);
  const [rackLength, setRackLength] = useState<number>(rack.rack_length || (unit === 'cm' ? 300 : 3));
  const [rackWidth, setRackWidth] = useState<number>(rack.rack_depth || (unit === 'cm' ? 100 : 1));
  const [rackHeight, setRackHeight] = useState<number | null>(rack.rack_height ?? null);
  const [saving, setSaving] = useState(false);

  const unitLabel = unit === 'cm' ? 'cm' : 'm';
  const minLength = unit === 'cm' ? 10 : 0.1;
  const maxLength = unit === 'cm' ? 2000 : 20;
  const stepLength = unit === 'cm' ? 10 : 0.1;
  const minWidth = unit === 'cm' ? 10 : 0.1;
  const maxWidth = unit === 'cm' ? 1000 : 10;
  const stepWidth = unit === 'cm' ? 10 : 0.1;

  const hasChanges =
    rackName !== (rack.rack_name || '') ||
    selectedZone !== (rack.zone_id || '') ||
    levels !== rack.levels ||
    bays !== rack.bays ||
    rackLength !== rack.rack_length ||
    rackWidth !== rack.rack_depth ||
    rackHeight !== (rack.rack_height ?? null);

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    if (!hasChanges) {
      onClose();
      return;
    }

    setSaving(true);
    await onSubmit({
      rack_name: rackName.trim() || '',
      zone_id: selectedZone || null,
      levels,
      bays,
      rack_length: rackLength,
      rack_depth: rackWidth,
      rack_height: rackHeight,
    });
    setSaving(false);
  };

  const currentZone = zones.find((z) => z.id === selectedZone);

  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center">
      <div className="absolute inset-0 bg-black/50 backdrop-blur-sm" onClick={onClose} />

      <div className="relative bg-white dark:bg-gray-800 rounded-2xl shadow-2xl w-full max-w-lg mx-4 overflow-hidden">
        <div className="bg-gradient-to-r from-emerald-600 to-teal-600 px-6 py-5">
          <div className="flex items-center justify-between">
            <div className="flex items-center gap-3">
              <div className="w-10 h-10 bg-white/20 rounded-xl flex items-center justify-center">
                <Grid3X3 className="h-5 w-5 text-white" />
              </div>
              <div>
                <h2 className="text-xl font-bold text-white">Edit {rack.rack_code}</h2>
                <p className="text-emerald-100 text-sm">Update rack configuration</p>
              </div>
            </div>
            <button
              onClick={onClose}
              className="p-2 rounded-lg text-white/80 hover:text-white hover:bg-white/10 transition-colors"
            >
              <X className="h-5 w-5" />
            </button>
          </div>
        </div>

        <form onSubmit={handleSubmit} className="p-6 space-y-6">
          <div className="grid grid-cols-2 gap-4">
            <div className="col-span-2">
              <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-2">
                Rack Name <span className="text-gray-400">(optional)</span>
              </label>
              <input
                type="text"
                value={rackName}
                onChange={(e) => setRackName(e.target.value)}
                placeholder="e.g., Electronics, Bulk Storage"
                className="w-full px-4 py-3 rounded-lg border border-gray-300 dark:border-gray-600 bg-white dark:bg-gray-700 text-gray-900 dark:text-gray-100 focus:outline-none focus:ring-2 focus:ring-emerald-500"
              />
            </div>

            <div className="col-span-2">
              <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-2">
                Assign to Zone
              </label>
              <select
                value={selectedZone}
                onChange={(e) => setSelectedZone(e.target.value)}
                className="w-full px-4 py-3 rounded-lg border border-gray-300 dark:border-gray-600 bg-white dark:bg-gray-700 text-gray-900 dark:text-gray-100 focus:outline-none focus:ring-2 focus:ring-emerald-500"
              >
                <option value="">No Zone (Unassigned)</option>
                {zones.map((zone) => (
                  <option key={zone.id} value={zone.id}>
                    Zone {zone.zone_code}
                    {zone.zone_name ? ` - ${zone.zone_name}` : ''}
                  </option>
                ))}
              </select>
              {currentZone && (
                <div className="flex items-center gap-2 mt-2 text-sm text-gray-500 dark:text-gray-400">
                  <span
                    className="w-3 h-3 rounded-full"
                    style={{ backgroundColor: currentZone.color }}
                  />
                  Currently in Zone {currentZone.zone_code}
                </div>
              )}
            </div>

            <div>
              <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-2">
                Length ({unitLabel})
              </label>
              <div className="flex items-center gap-2">
                <button
                  type="button"
                  onClick={() => setRackLength(Math.max(minLength, rackLength - stepLength))}
                  className="w-10 h-10 rounded-lg border border-gray-300 dark:border-gray-600 flex items-center justify-center text-gray-600 dark:text-gray-400 hover:bg-gray-100 dark:hover:bg-gray-700 transition-colors"
                >
                  -
                </button>
                <input
                  type="number"
                  min={minLength}
                  max={maxLength}
                  step={stepLength}
                  value={rackLength}
                  onChange={(e) => setRackLength(Math.max(minLength, Math.min(maxLength, parseFloat(e.target.value) || minLength)))}
                  className="flex-1 px-4 py-2 rounded-lg border border-gray-300 dark:border-gray-600 bg-white dark:bg-gray-700 text-gray-900 dark:text-gray-100 text-center focus:outline-none focus:ring-2 focus:ring-emerald-500"
                />
                <button
                  type="button"
                  onClick={() => setRackLength(Math.min(maxLength, rackLength + stepLength))}
                  className="w-10 h-10 rounded-lg border border-gray-300 dark:border-gray-600 flex items-center justify-center text-gray-600 dark:text-gray-400 hover:bg-gray-100 dark:hover:bg-gray-700 transition-colors"
                >
                  +
                </button>
              </div>
            </div>

            <div>
              <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-2">
                Width ({unitLabel})
              </label>
              <div className="flex items-center gap-2">
                <button
                  type="button"
                  onClick={() => setRackWidth(Math.max(minWidth, rackWidth - stepWidth))}
                  className="w-10 h-10 rounded-lg border border-gray-300 dark:border-gray-600 flex items-center justify-center text-gray-600 dark:text-gray-400 hover:bg-gray-100 dark:hover:bg-gray-700 transition-colors"
                >
                  -
                </button>
                <input
                  type="number"
                  min={minWidth}
                  max={maxWidth}
                  step={stepWidth}
                  value={rackWidth}
                  onChange={(e) => setRackWidth(Math.max(minWidth, Math.min(maxWidth, parseFloat(e.target.value) || minWidth)))}
                  className="flex-1 px-4 py-2 rounded-lg border border-gray-300 dark:border-gray-600 bg-white dark:bg-gray-700 text-gray-900 dark:text-gray-100 text-center focus:outline-none focus:ring-2 focus:ring-emerald-500"
                />
                <button
                  type="button"
                  onClick={() => setRackWidth(Math.min(maxWidth, rackWidth + stepWidth))}
                  className="w-10 h-10 rounded-lg border border-gray-300 dark:border-gray-600 flex items-center justify-center text-gray-600 dark:text-gray-400 hover:bg-gray-100 dark:hover:bg-gray-700 transition-colors"
                >
                  +
                </button>
              </div>
            </div>

            <div className="col-span-2">
              <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-2">
                Height ({unitLabel}) <span className="text-gray-400">(optional)</span>
              </label>
              <div className="flex items-center gap-2">
                <button
                  type="button"
                  onClick={() => setRackHeight(rackHeight !== null ? Math.max(minLength, rackHeight - stepLength) : null)}
                  disabled={rackHeight === null}
                  className="w-10 h-10 rounded-lg border border-gray-300 dark:border-gray-600 flex items-center justify-center text-gray-600 dark:text-gray-400 hover:bg-gray-100 dark:hover:bg-gray-700 transition-colors disabled:opacity-50"
                >
                  -
                </button>
                <input
                  type="number"
                  min={minLength}
                  max={maxLength}
                  step={stepLength}
                  value={rackHeight ?? ''}
                  placeholder="Not set"
                  onChange={(e) => setRackHeight(e.target.value ? parseFloat(e.target.value) : null)}
                  className="flex-1 px-4 py-2 rounded-lg border border-gray-300 dark:border-gray-600 bg-white dark:bg-gray-700 text-gray-900 dark:text-gray-100 text-center focus:outline-none focus:ring-2 focus:ring-emerald-500"
                />
                <button
                  type="button"
                  onClick={() => setRackHeight(rackHeight !== null ? Math.min(maxLength, rackHeight + stepLength) : (unit === 'cm' ? 200 : 2))}
                  className="w-10 h-10 rounded-lg border border-gray-300 dark:border-gray-600 flex items-center justify-center text-gray-600 dark:text-gray-400 hover:bg-gray-100 dark:hover:bg-gray-700 transition-colors"
                >
                  +
                </button>
                {rackHeight !== null && (
                  <button
                    type="button"
                    onClick={() => setRackHeight(null)}
                    className="px-3 py-2 text-sm text-gray-500 hover:text-red-500 transition-colors"
                  >
                    Clear
                  </button>
                )}
              </div>
              <p className="mt-1 text-xs text-gray-500 dark:text-gray-400">Physical rack height (not shown on 2D map)</p>
            </div>

            <div>
              <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-2">
                Number of Levels
              </label>
              <div className="flex items-center gap-2">
                <button
                  type="button"
                  onClick={() => setLevels(Math.max(1, levels - 1))}
                  className="w-10 h-10 rounded-lg border border-gray-300 dark:border-gray-600 flex items-center justify-center text-gray-600 dark:text-gray-400 hover:bg-gray-100 dark:hover:bg-gray-700 transition-colors"
                >
                  -
                </button>
                <input
                  type="number"
                  min={1}
                  max={20}
                  value={levels}
                  onChange={(e) => setLevels(Math.max(1, Math.min(20, parseInt(e.target.value) || 1)))}
                  className="flex-1 px-4 py-2 rounded-lg border border-gray-300 dark:border-gray-600 bg-white dark:bg-gray-700 text-gray-900 dark:text-gray-100 text-center focus:outline-none focus:ring-2 focus:ring-emerald-500"
                />
                <button
                  type="button"
                  onClick={() => setLevels(Math.min(20, levels + 1))}
                  className="w-10 h-10 rounded-lg border border-gray-300 dark:border-gray-600 flex items-center justify-center text-gray-600 dark:text-gray-400 hover:bg-gray-100 dark:hover:bg-gray-700 transition-colors"
                >
                  +
                </button>
              </div>
            </div>

            <div>
              <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-2">
                Number of Bays
              </label>
              <div className="flex items-center gap-2">
                <button
                  type="button"
                  onClick={() => setBays(Math.max(1, bays - 1))}
                  className="w-10 h-10 rounded-lg border border-gray-300 dark:border-gray-600 flex items-center justify-center text-gray-600 dark:text-gray-400 hover:bg-gray-100 dark:hover:bg-gray-700 transition-colors"
                >
                  -
                </button>
                <input
                  type="number"
                  min={1}
                  max={50}
                  value={bays}
                  onChange={(e) => setBays(Math.max(1, Math.min(50, parseInt(e.target.value) || 1)))}
                  className="flex-1 px-4 py-2 rounded-lg border border-gray-300 dark:border-gray-600 bg-white dark:bg-gray-700 text-gray-900 dark:text-gray-100 text-center focus:outline-none focus:ring-2 focus:ring-emerald-500"
                />
                <button
                  type="button"
                  onClick={() => setBays(Math.min(50, bays + 1))}
                  className="w-10 h-10 rounded-lg border border-gray-300 dark:border-gray-600 flex items-center justify-center text-gray-600 dark:text-gray-400 hover:bg-gray-100 dark:hover:bg-gray-700 transition-colors"
                >
                  +
                </button>
              </div>
            </div>
          </div>

          <div className="bg-amber-50 dark:bg-amber-900/20 border border-amber-200 dark:border-amber-800 rounded-lg p-4">
            <p className="text-sm text-amber-800 dark:text-amber-300">
              <strong>Note:</strong> Changing levels or bays may affect existing bin assignments.
              Current capacity: {rack.levels * rack.bays} bins. New capacity:{' '}
              {levels * bays} bins.
            </p>
          </div>

          <div className="flex gap-3">
            <Button type="button" variant="secondary" onClick={onClose} className="flex-1">
              Cancel
            </Button>
            <Button
              type="submit"
              variant="primary"
              loading={saving}
              disabled={!hasChanges}
              className="flex-1"
            >
              Save Changes
            </Button>
          </div>
        </form>
      </div>
    </div>
  );
};

interface DeleteRackModalProps {
  rack: WarehouseRack;
  binCount: number;
  zone: WarehouseZone | undefined;
  onConfirm: () => Promise<boolean>;
  onCancel: () => void;
}

const DeleteRackModal: React.FC<DeleteRackModalProps> = ({
  rack,
  binCount,
  zone,
  onConfirm,
  onCancel,
}) => {
  const [deleting, setDeleting] = useState(false);

  const handleConfirm = async () => {
    setDeleting(true);
    await onConfirm();
    setDeleting(false);
  };

  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center">
      <div className="absolute inset-0 bg-black/50 backdrop-blur-sm" onClick={onCancel} />

      <div className="relative bg-white dark:bg-gray-800 rounded-2xl shadow-2xl w-full max-w-md mx-4 overflow-hidden">
        <div className="bg-red-600 px-6 py-5">
          <div className="flex items-center justify-between">
            <div className="flex items-center gap-3">
              <div className="w-10 h-10 bg-white/20 rounded-xl flex items-center justify-center">
                <AlertTriangle className="h-5 w-5 text-white" />
              </div>
              <div>
                <h2 className="text-xl font-bold text-white">Delete Rack</h2>
                <p className="text-red-100 text-sm">This action cannot be undone</p>
              </div>
            </div>
            <button
              onClick={onCancel}
              className="p-2 rounded-lg text-white/80 hover:text-white hover:bg-white/10 transition-colors"
            >
              <X className="h-5 w-5" />
            </button>
          </div>
        </div>

        <div className="p-6 space-y-4">
          <div className="flex items-center gap-4">
            <div className="w-12 h-12 bg-emerald-100 dark:bg-emerald-900/30 rounded-xl flex items-center justify-center">
              <Grid3X3 className="h-6 w-6 text-emerald-600 dark:text-emerald-400" />
            </div>
            <div>
              <div className="font-semibold text-gray-900 dark:text-gray-100">
                {rack.rack_code}
                {rack.rack_name && ` - ${rack.rack_name}`}
              </div>
              <div className="text-sm text-gray-500 dark:text-gray-400 flex items-center gap-2">
                {zone ? (
                  <>
                    <span
                      className="w-2.5 h-2.5 rounded-full"
                      style={{ backgroundColor: zone.color }}
                    />
                    Zone {zone.zone_code}
                  </>
                ) : (
                  'Unassigned'
                )}
                <ChevronRight className="h-3 w-3" />
                {rack.levels} levels x {rack.bays} bays
              </div>
            </div>
          </div>

          {binCount > 0 && (
            <div className="bg-red-50 dark:bg-red-900/20 border border-red-200 dark:border-red-800 rounded-lg p-4">
              <p className="text-sm text-red-800 dark:text-red-300">
                <strong>Warning:</strong> This rack has {binCount} bin{binCount !== 1 ? 's' : ''}{' '}
                assigned. Deleting this rack will also delete all associated bins.
              </p>
            </div>
          )}

          <p className="text-gray-600 dark:text-gray-400">
            Are you sure you want to delete this rack? This action cannot be undone.
          </p>

          <div className="flex gap-3 pt-2">
            <Button type="button" variant="secondary" onClick={onCancel} className="flex-1">
              Cancel
            </Button>
            <Button
              type="button"
              variant="danger"
              onClick={handleConfirm}
              loading={deleting}
              className="flex-1"
            >
              Delete Rack
            </Button>
          </div>
        </div>
      </div>
    </div>
  );
};

interface DuplicateRackModalProps {
  rack: WarehouseRack;
  zone: WarehouseZone | undefined;
  existingRacks: WarehouseRack[];
  onConfirm: (count: number) => Promise<boolean>;
  onCancel: () => void;
}

const DuplicateRackModal: React.FC<DuplicateRackModalProps> = ({
  rack,
  zone,
  existingRacks,
  onConfirm,
  onCancel,
}) => {
  const [count, setCount] = useState(1);
  const [duplicating, setDuplicating] = useState(false);

  const maxCount = 20;
  const minCount = 1;

  const getPreviewNames = () => {
    const names: string[] = [];
    let nameSuffix = 1;

    if (rack.rack_name) {
      const existingNames = existingRacks
        .filter(r => r.rack_name?.startsWith(rack.rack_name!))
        .map(r => {
          const match = r.rack_name?.match(new RegExp(`^${rack.rack_name!.replace(/[.*+?^${}()|[\]\\]/g, '\\$&')}-(\\d+)$`));
          return match ? parseInt(match[1], 10) : 0;
        });
      nameSuffix = existingNames.length > 0 ? Math.max(...existingNames) + 1 : 1;
    }

    const existingNumbers = existingRacks.map(r => r.rack_number).sort((a, b) => a - b);
    let nextNumber = 1;
    for (let i = 1; i <= existingNumbers.length + count; i++) {
      if (!existingNumbers.includes(i)) {
        nextNumber = i;
        break;
      }
    }
    if (existingNumbers.length > 0 && nextNumber === 1) {
      nextNumber = existingNumbers[existingNumbers.length - 1] + 1;
    }

    const usedNumbers = new Set(existingNumbers);
    let currentNumber = nextNumber;

    for (let i = 0; i < count; i++) {
      while (usedNumbers.has(currentNumber)) {
        currentNumber++;
      }
      usedNumbers.add(currentNumber);

      const rackCode = `RACK-${String(currentNumber).padStart(2, '0')}`;
      const displayName = rack.rack_name
        ? `${rack.rack_name}-${nameSuffix + i}`
        : rackCode;
      names.push(displayName);
      currentNumber++;
    }

    return names;
  };

  const previewNames = getPreviewNames();

  const handleConfirm = async () => {
    setDuplicating(true);
    await onConfirm(count);
    setDuplicating(false);
  };

  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center">
      <div className="absolute inset-0 bg-black/50 backdrop-blur-sm" onClick={onCancel} />

      <div className="relative bg-white dark:bg-gray-800 rounded-2xl shadow-2xl w-full max-w-md mx-4 overflow-hidden">
        <div className="bg-gradient-to-r from-cyan-600 to-teal-600 px-6 py-5">
          <div className="flex items-center justify-between">
            <div className="flex items-center gap-3">
              <div className="w-10 h-10 bg-white/20 rounded-xl flex items-center justify-center">
                <Copy className="h-5 w-5 text-white" />
              </div>
              <div>
                <h2 className="text-xl font-bold text-white">Duplicate Rack</h2>
                <p className="text-cyan-100 text-sm">Create copies of {rack.rack_code}</p>
              </div>
            </div>
            <button
              onClick={onCancel}
              className="p-2 rounded-lg text-white/80 hover:text-white hover:bg-white/10 transition-colors"
            >
              <X className="h-5 w-5" />
            </button>
          </div>
        </div>

        <div className="p-6 space-y-5">
          <div className="flex items-center gap-4">
            <div className="w-12 h-12 bg-emerald-100 dark:bg-emerald-900/30 rounded-xl flex items-center justify-center">
              <Grid3X3 className="h-6 w-6 text-emerald-600 dark:text-emerald-400" />
            </div>
            <div>
              <div className="font-semibold text-gray-900 dark:text-gray-100">
                {rack.rack_code}
                {rack.rack_name && ` - ${rack.rack_name}`}
              </div>
              <div className="text-sm text-gray-500 dark:text-gray-400 flex items-center gap-2">
                {zone ? (
                  <>
                    <span
                      className="w-2.5 h-2.5 rounded-full"
                      style={{ backgroundColor: zone.color }}
                    />
                    Zone {zone.zone_code}
                  </>
                ) : (
                  'Unassigned'
                )}
                <ChevronRight className="h-3 w-3" />
                {rack.levels} levels x {rack.bays} bays
              </div>
            </div>
          </div>

          <div>
            <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-3">
              Number of Copies
            </label>
            <div className="flex items-center gap-3">
              <button
                type="button"
                onClick={() => setCount(Math.max(minCount, count - 1))}
                disabled={count <= minCount}
                className="w-12 h-12 rounded-xl border border-gray-300 dark:border-gray-600 flex items-center justify-center text-xl font-medium text-gray-600 dark:text-gray-400 hover:bg-gray-100 dark:hover:bg-gray-700 transition-colors disabled:opacity-50 disabled:cursor-not-allowed"
              >
                -
              </button>
              <input
                type="number"
                min={minCount}
                max={maxCount}
                value={count}
                onChange={(e) => setCount(Math.max(minCount, Math.min(maxCount, parseInt(e.target.value) || minCount)))}
                className="flex-1 px-4 py-3 rounded-xl border border-gray-300 dark:border-gray-600 bg-white dark:bg-gray-700 text-gray-900 dark:text-gray-100 text-center text-lg font-medium focus:outline-none focus:ring-2 focus:ring-cyan-500"
              />
              <button
                type="button"
                onClick={() => setCount(Math.min(maxCount, count + 1))}
                disabled={count >= maxCount}
                className="w-12 h-12 rounded-xl border border-gray-300 dark:border-gray-600 flex items-center justify-center text-xl font-medium text-gray-600 dark:text-gray-400 hover:bg-gray-100 dark:hover:bg-gray-700 transition-colors disabled:opacity-50 disabled:cursor-not-allowed"
              >
                +
              </button>
            </div>
            <p className="mt-2 text-xs text-gray-500 dark:text-gray-400">
              Maximum {maxCount} copies at once
            </p>
          </div>

          <div className="bg-gray-50 dark:bg-gray-700/50 rounded-xl p-4">
            <div className="text-sm font-medium text-gray-700 dark:text-gray-300 mb-3">
              Preview ({count} new rack{count !== 1 ? 's' : ''})
            </div>
            <div className="space-y-2 max-h-40 overflow-y-auto">
              {previewNames.map((name, index) => (
                <div
                  key={index}
                  className="flex items-center gap-2 text-sm text-gray-600 dark:text-gray-400"
                >
                  <div className="w-6 h-6 bg-cyan-100 dark:bg-cyan-900/30 rounded-md flex items-center justify-center flex-shrink-0">
                    <span className="text-xs font-medium text-cyan-600 dark:text-cyan-400">
                      {index + 1}
                    </span>
                  </div>
                  <span className="truncate">{name}</span>
                </div>
              ))}
            </div>
          </div>

          <div className="bg-blue-50 dark:bg-blue-900/20 border border-blue-200 dark:border-blue-800 rounded-lg p-4">
            <p className="text-sm text-blue-800 dark:text-blue-300">
              Each copy will have the same configuration ({rack.levels} levels, {rack.bays} bays)
              and will be positioned with an offset to avoid overlap.
            </p>
          </div>

          <div className="flex gap-3 pt-2">
            <Button type="button" variant="secondary" onClick={onCancel} className="flex-1">
              Cancel
            </Button>
            <Button
              type="button"
              variant="primary"
              onClick={handleConfirm}
              loading={duplicating}
              className="flex-1"
            >
              Create {count} Copy{count !== 1 ? 'ies' : ''}
            </Button>
          </div>
        </div>
      </div>
    </div>
  );
};
